/**
 * Device-local override layer for workspace OUTPUT settings (spec 019 Wave-2 Task 4).
 *
 * A Customer's output preferences (format, quality, width/height, max-canvas, presets) are
 * saved to THIS DEVICE ONLY — never synced to the cloud — so one operator's local choice can
 * never overwrite another device's or the tenant/global defaults. Values live in the injected
 * `LocalStore.kv` (desktop: encrypted device store; web: IndexedDB, memory-backed fallback)
 * under keys `genz.settings.local.<key>`.
 *
 * `resolveWorkspaceSettings` is the SINGLE authority that resolves the effective value of each
 * output key across four layers, per-key, with provenance:
 *
 *   device-local override → tenant setting → platform-global default → hardcoded default
 *
 * A layer counts as ABSENT when its value is null/undefined or an empty array (so a tenant's
 * default empty preset list lets a super-admin's global presets surface).
 */
import { currentPlatform, type LocalStore } from "@genz/platform";
import type { ResolvedWorkspaceSettings, WorkspaceOutputSettings } from "@genz/platform";

/** The output keys resolved + stored device-locally. Order is stable (UI iterates it). */
export const OUTPUT_KEYS = [
  "output_format",
  "output_quality",
  "output_width",
  "output_height",
  "max_canvas_px",
  "output_presets",
] as const;
export type OutputKey = (typeof OUTPUT_KEYS)[number];

/** KV namespace for a device-local override; one entry per output key. */
export const LOCAL_SETTINGS_PREFIX = "genz.settings.local.";

/** The last-resort defaults when no tenant/global value exists (mirror the cloud defaults;
 *  max_canvas_px matches `DEFAULT_MAX_CANVAS_PX` in cloud/app/settings/router.py). */
export const HARDCODED_OUTPUT_DEFAULTS: WorkspaceOutputSettings = {
  output_format: "png",
  output_quality: 90,
  output_width: 2000,
  output_height: 2000,
  max_canvas_px: 3000,
  output_presets: [],
};

export type SettingSource = "local" | "tenant" | "global" | "default";
export interface ResolvedValue<T> {
  value: T;
  source: SettingSource;
}
export type ResolvedOutputSettings = {
  [K in OutputKey]: ResolvedValue<WorkspaceOutputSettings[K]>;
};

/**
 * PRESENCE for the TENANT / GLOBAL layers: a value counts as present unless it is null/
 * undefined or an EMPTY ARRAY — so a tenant's default empty preset list lets a super-admin's
 * global presets surface. This must NOT be used for the local layer (see `localPresent`).
 */
function present(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

/**
 * PRESENCE for the DEVICE-LOCAL layer: a stored key is an INTENTIONAL override even when its
 * value is an empty array / empty string / 0. Only null/undefined (or an absent key) fall
 * through — so a customer who deletes all their presets locally keeps an empty list instead of
 * re-inheriting the tenant/global presets.
 */
function localPresent(map: Record<string, unknown>, key: string): boolean {
  return key in map && map[key] !== null && map[key] !== undefined;
}

function kvOf(store?: LocalStore) {
  return (store ?? currentPlatform()?.localStore)?.kv;
}

/** Read every device-local output override present in the store (absent keys omitted). */
export async function getLocalOverrides(store?: LocalStore): Promise<Partial<WorkspaceOutputSettings>> {
  const kv = kvOf(store);
  if (!kv) return {};
  const out: Record<string, unknown> = {};
  for (const key of OUTPUT_KEYS) {
    const v = await kv.get(`${LOCAL_SETTINGS_PREFIX}${key}`);
    if (v !== undefined && v !== null) out[key] = v;
  }
  return out as Partial<WorkspaceOutputSettings>;
}

/** Write (or, when a key's value is `undefined`, clear) the given device-local overrides. */
export async function setLocalOverrides(
  patch: Partial<WorkspaceOutputSettings>,
  store?: LocalStore,
): Promise<void> {
  const kv = kvOf(store);
  if (!kv) return;
  for (const key of OUTPUT_KEYS) {
    if (!(key in patch)) continue;
    const v = (patch as Record<string, unknown>)[key];
    if (v === undefined) await kv.delete(`${LOCAL_SETTINGS_PREFIX}${key}`);
    else await kv.set(`${LOCAL_SETTINGS_PREFIX}${key}`, v);
  }
}

/** Remove a single device-local override so the key reverts to the tenant/global/default. */
export async function clearLocalOverride(key: OutputKey, store?: LocalStore): Promise<void> {
  const kv = kvOf(store);
  if (!kv) return;
  await kv.delete(`${LOCAL_SETTINGS_PREFIX}${key}`);
}

/**
 * Resolve every output key across the four layers, with per-key provenance. `cloud` carries
 * the tenant values (top-level) and the platform-global defaults (`global_defaults`); `local`
 * is the device-local override map. This is the ONLY place the precedence lives.
 */
export function resolveWorkspaceSettings(
  cloud: Partial<ResolvedWorkspaceSettings> | null | undefined,
  local: Partial<WorkspaceOutputSettings>,
): ResolvedOutputSettings {
  const tenant = (cloud ?? {}) as Record<string, unknown>;
  const global = (cloud?.global_defaults ?? {}) as Record<string, unknown>;
  const localMap = local as Record<string, unknown>;
  const out = {} as Record<string, ResolvedValue<unknown>>;
  for (const key of OUTPUT_KEYS) {
    if (localPresent(localMap, key)) out[key] = { value: localMap[key], source: "local" };
    else if (present(tenant[key])) out[key] = { value: tenant[key], source: "tenant" };
    else if (present(global[key])) out[key] = { value: global[key], source: "global" };
    else out[key] = { value: HARDCODED_OUTPUT_DEFAULTS[key], source: "default" };
  }
  return out as ResolvedOutputSettings;
}

/** Flatten a resolved map to plain values (for the render encode / preset selection). */
export function resolvedOutputValues(r: ResolvedOutputSettings): WorkspaceOutputSettings {
  return {
    output_format: r.output_format.value,
    output_quality: r.output_quality.value,
    output_width: r.output_width.value,
    output_height: r.output_height.value,
    max_canvas_px: r.max_canvas_px.value,
    output_presets: r.output_presets.value,
  };
}
